/**
 * Remember the id of the publish job a user kicked off for a given document.
 *
 * The acceptance criterion is: "Leaving the editor during a job and coming
 * back shows the current state." If the user navigates away mid-publish or
 * the tab reloads, the editor needs to be able to recognise an in-flight
 * job and poll its status again. localStorage so a different tab for the
 * same document can also see the job.
 */

const KEY_PREFIX = 'publish-job';

function keyFor(sessionId: string, documentId: string): string {
  return `${KEY_PREFIX}:${sessionId}:${documentId}`;
}

export interface RememberedPublishJob {
  sessionId: string;
  documentId: string;
  jobId: string;
}

/**
 * Scan localStorage for every publish-job key we remembered. Used by the
 * global watcher to poll in-flight jobs across documents.
 */
export function listRememberedPublishJobs(): RememberedPublishJob[] {
  const out: RememberedPublishJob[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${KEY_PREFIX}:`)) continue;
      const [, sessionId, documentId] = key.split(':');
      const jobId = localStorage.getItem(key);
      if (sessionId && documentId && jobId) {
        out.push({ sessionId, documentId, jobId });
      }
    }
  } catch {
    // Private mode / no storage — nothing to resume.
  }
  return out;
}

export function rememberPublishJob(
  sessionId: string,
  documentId: string,
  jobId: string
): void {
  try {
    localStorage.setItem(keyFor(sessionId, documentId), jobId);
  } catch {
    // Private mode / quota — not critical. The job still runs server-side.
  }
}

export function forgetPublishJob(sessionId: string, documentId: string): void {
  try {
    localStorage.removeItem(keyFor(sessionId, documentId));
  } catch {
    // See above.
  }
}

export function recallPublishJob(
  sessionId: string,
  documentId: string
): string | null {
  try {
    return localStorage.getItem(keyFor(sessionId, documentId));
  } catch {
    return null;
  }
}
